import { Check, Minus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getApiErrorMessage,
  getCrossSystemPresence,
  getLinkedProfileFull,
  getLinkedProfileSiblings,
  getLinkedProfileTickets,
} from '../api/client';
import type {
  CrossSystemPresence,
  LinkedProfileChild,
  LinkedProfileFull,
  LinkedProfileTicketsResult,
} from '../api/types';
import ProfileTicketRow, { ProfileTicketRowsSkeleton } from './ProfileTicketRow';

type Tab = 'overview' | 'enrollments' | 'courses' | 'payments' | 'platforms';

type Props = {
  ticketId: string;
  currentTicketId?: string;
  linkedLearnerId?: string | null;
  linkedLearnerName?: string | null;
  linkedEnrollmentId?: string | null;
  onTicketNavigate?: () => void;
};

function normalizeProfileFull(data: LinkedProfileFull): LinkedProfileFull {
  return {
    ...data,
    enrollments: data.enrollments ?? [],
    courses: data.courses ?? [],
    payments: data.payments ?? [],
  };
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function ProfileField({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="profile-field-row">
      <span className="profile-field-label">{label}</span>
      <span className="profile-field-value">{value}</span>
    </div>
  );
}

function OverviewTab({
  profile,
  profileTickets,
  profileTicketsLoading,
  siblings,
  siblingsLoading,
  linkedLearnerId,
  linkedLearnerName,
  linkedEnrollmentId,
  currentTicketId,
  onTicketNavigate,
}: {
  profile: LinkedProfileFull;
  profileTickets: LinkedProfileTicketsResult | null;
  profileTicketsLoading: boolean;
  siblings: LinkedProfileChild[];
  siblingsLoading: boolean;
  linkedLearnerId?: string | null;
  linkedLearnerName?: string | null;
  linkedEnrollmentId?: string | null;
  currentTicketId?: string;
  onTicketNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const [siblingsOpen, setSiblingsOpen] = useState(false);
  const openTickets = profileTickets?.openTickets ?? [];
  const openTicketIds = new Set(openTickets.map((t) => t.id));
  const visibleOpen = openTickets.slice(0, 5);
  const recentTickets = (profileTickets?.recentTickets ?? []).filter((t) => !openTicketIds.has(t.id));
  const visibleRecent = recentTickets.slice(0, 5);

  const aboutName = linkedLearnerName ?? profile.learnerName;
  const aboutGrade = profile.grade;
  const aboutPathway = profile.curriculum;
  const aboutEnrollment = linkedEnrollmentId ?? profile.enrollmentId;

  return (
    <div className="profile-full-overview">
      {linkedLearnerId ? (
        <div className="profile-ticket-about-box">
          <div className="profile-ticket-about-label">This ticket is about</div>
          <div className="profile-ticket-about-name">{aboutName}</div>
          <div className="profile-ticket-about-meta">
            {[aboutGrade, aboutPathway].filter(Boolean).join(' · ')}
          </div>
          {aboutEnrollment && (
            <div className="profile-ticket-about-enrollment">
              Enrollment: {aboutEnrollment}
            </div>
          )}
        </div>
      ) : (
        <p className="ticket-about-muted">Not linked to a specific child</p>
      )}

      {!siblingsLoading && siblings.length > 0 && (
        <div className="profile-siblings-section">
          <button
            type="button"
            className="profile-siblings-toggle"
            onClick={() => setSiblingsOpen((v) => !v)}
            aria-expanded={siblingsOpen}
          >
            Other children ({siblings.length})
          </button>
          {siblingsOpen && (
            <ul className="profile-siblings-list">
              {siblings.map((s) => (
                <li key={s.learnerId}>
                  <strong>{s.learnerName}</strong>
                  <span>
                    {[s.grade, s.enrollmentId].filter(Boolean).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="profile-learner-name">{profile.learnerName}</div>
      {profile.parentName && <div className="profile-parent-name">{profile.parentName}</div>}
      {(profile.email || profile.phone) && (
        <div className="profile-contact-lines profile-contact-lines--compact">
          {profile.email && (
            <div className="profile-contact-line">
              <span className="profile-contact-line-text">{profile.email}</span>
            </div>
          )}
          {profile.phone && (
            <div className="profile-contact-line">
              <span className="profile-contact-line-text">{profile.phone}</span>
            </div>
          )}
        </div>
      )}
      <div className="profile-fields profile-drawer-summary-grid">
        <ProfileField label="Enrollment ID" value={profile.enrollmentId} />
        <ProfileField label="Grade" value={profile.grade} />
        <ProfileField label="Entity" value={profile.entity} />
        <ProfileField label="Pathway" value={profile.curriculum} />
      </div>
      {profile.erpData && (
        <div className="profile-fields profile-fields--spaced">
          <ProfileField label="Session" value={profile.erpData.session} />
          <ProfileField label="Batch" value={profile.erpData.batch} />
        </div>
      )}
      {profile.erpData?.additionalCourses && profile.erpData.additionalCourses.length > 0 && (
        <div className="profile-extra-list">
          <div className="profile-field-label">Additional courses</div>
          <ul>
            {profile.erpData.additionalCourses.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {profileTicketsLoading && <ProfileTicketRowsSkeleton count={3} />}

      {!profileTicketsLoading && openTickets.length > 0 && (
        <div className="profile-ticket-section">
          <div className="profile-ticket-section-label">
            Open tickets ({openTickets.length})
          </div>
          {visibleOpen.map((t) => (
            <ProfileTicketRow
              key={t.id}
              ticket={t}
              currentTicketId={currentTicketId}
              onNavigate={onTicketNavigate}
            />
          ))}
          {openTickets.length > 5 && (
            <button
              type="button"
              className="profile-ticket-view-all"
              onClick={() => {
                const q = (profile.enrollmentId ?? '').trim();
                navigate(q ? `/tickets?search=${encodeURIComponent(q)}` : '/tickets');
                onTicketNavigate?.();
              }}
            >
              View all →
            </button>
          )}
        </div>
      )}

      {!profileTicketsLoading && visibleRecent.length > 0 && (
        <div className="profile-ticket-section">
          <div className="profile-ticket-section-label">Recent tickets</div>
          {visibleRecent.map((t) => (
            <ProfileTicketRow
              key={t.id}
              ticket={t}
              currentTicketId={currentTicketId}
              showCurrentLabel
              onNavigate={onTicketNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EnrollmentsTab({ profile }: { profile: LinkedProfileFull }) {
  const enrollments = profile.enrollments ?? [];
  if (enrollments.length === 0) {
    return <p className="text-muted">No enrollments found.</p>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table profile-full-table">
        <thead>
          <tr>
            <th>Enrollment No.</th>
            <th>School</th>
            <th>Curriculum</th>
            <th>Grade</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {enrollments.map((e) => (
            <tr key={e.id}>
              <td>{e.enrollmentNumber}</td>
              <td>{e.school || '—'}</td>
              <td>{e.curriculum || '—'}</td>
              <td>{e.grade || '—'}</td>
              <td>{e.status || '—'}</td>
              <td>{e.enrollmentDate ? formatDate(e.enrollmentDate) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CoursesTab({ profile }: { profile: LinkedProfileFull }) {
  const courses = profile.courses ?? [];
  if (courses.length === 0) {
    return <p className="text-muted">No courses found.</p>;
  }
  return (
    <ul className="profile-course-list">
      {courses.map((course, i) => (
        <li key={`${course.name}-${i}`} className="profile-course-row">
          <span className="profile-course-name">{course.name}</span>
          <span className="badge badge-neutral">{course.status}</span>
          {course.sessionsRemaining !== undefined && (
            <span className="profile-course-meta">{course.sessionsRemaining} sessions left</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function PaymentsTab({ profile }: { profile: LinkedProfileFull }) {
  const payments = profile.payments ?? [];
  if (payments.length === 0) {
    return <p className="text-muted">No payments found.</p>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table profile-full-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Receipt No.</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id}>
              <td>{formatDate(p.date)}</td>
              <td>{p.type || '—'}</td>
              <td className="col-tabular">{formatAmount(p.amount)}</td>
              <td>{p.status}</td>
              <td>{p.receiptNumber ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlatformsTab({
  presence,
  loading,
  error,
}: {
  presence: CrossSystemPresence | null;
  loading: boolean;
  error: string;
}) {
  if (loading) {
    return <div className="profile-loading-spinner">Loading…</div>;
  }
  if (error) {
    return <p className="profile-unavailable">{error}</p>;
  }
  if (!presence) {
    return null;
  }

  const cards = [
    { label: '21K ERP / Parenthub', present: presence.inERP },
    { label: '21K FLOWW', present: presence.inFLOWW },
    { label: '21K Garages', present: presence.inGarages },
  ];

  return (
    <div className="profile-platform-cards">
      {cards.map((card) => (
        <div key={card.label} className="profile-platform-card">
          <span className="profile-platform-icon" aria-hidden>
            {card.present ? (
              <Check size={18} className="profile-platform-check" />
            ) : (
              <Minus size={18} className="profile-platform-miss" />
            )}
          </span>
          <div>
            <div className="profile-platform-name">{card.label}</div>
            <div className={card.present ? 'profile-platform-present' : 'profile-platform-absent'}>
              {card.present ? 'Present' : 'Not found'}
            </div>
          </div>
        </div>
      ))}
      {presence.parenthubViaGarages && (
        <p className="profile-platform-note">21K student via Garages</p>
      )}
    </div>
  );
}

export default function LinkedProfileTabbedView({
  ticketId,
  currentTicketId,
  linkedLearnerId,
  linkedLearnerName,
  linkedEnrollmentId,
  onTicketNavigate,
}: Props) {
  const [tab, setTab] = useState<Tab>('overview');
  const [profile, setProfile] = useState<LinkedProfileFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileTickets, setProfileTickets] = useState<LinkedProfileTicketsResult | null>(null);
  const [profileTicketsLoading, setProfileTicketsLoading] = useState(false);
  const [presence, setPresence] = useState<CrossSystemPresence | null>(null);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [presenceError, setPresenceError] = useState('');
  const [presenceLoaded, setPresenceLoaded] = useState(false);
  const [siblings, setSiblings] = useState<LinkedProfileChild[]>([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    void getLinkedProfileFull(ticketId)
      .then((data) => {
        if (!cancelled) setProfile(data ? normalizeProfileFull(data) : null);
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  useEffect(() => {
    if (tab !== 'overview' || loading || !profile) return;

    let cancelled = false;
    setProfileTickets(null);
    setProfileTicketsLoading(true);

    void getLinkedProfileTickets(ticketId)
      .then((data) => {
        if (!cancelled) setProfileTickets(data);
      })
      .catch(() => {
        if (!cancelled) setProfileTickets({ openTickets: [], recentTickets: [] });
      })
      .finally(() => {
        if (!cancelled) setProfileTicketsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, loading, profile, ticketId]);

  useEffect(() => {
    if (tab !== 'overview' || loading || !profile) return;

    let cancelled = false;
    setSiblings([]);
    setSiblingsLoading(true);

    void getLinkedProfileSiblings(ticketId)
      .then((data) => {
        if (!cancelled) setSiblings(data.siblings);
      })
      .catch(() => {
        if (!cancelled) setSiblings([]);
      })
      .finally(() => {
        if (!cancelled) setSiblingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, loading, profile, ticketId]);

  useEffect(() => {
    if (tab !== 'platforms' || presenceLoaded) return;

    let cancelled = false;
    setPresenceLoading(true);
    setPresenceError('');

    void getCrossSystemPresence(ticketId)
      .then((data) => {
        if (!cancelled) {
          setPresence(data);
          setPresenceLoaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) setPresenceError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setPresenceLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tab, ticketId, presenceLoaded]);

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'enrollments', label: 'Enrollments' },
    { id: 'courses', label: 'Courses' },
    { id: 'payments', label: 'Payments' },
    { id: 'platforms', label: 'Other' },
  ];

  return (
    <div className="profile-drawer-tabs">
      <div className="slide-over-tabs" role="tablist" aria-label="Customer profile sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`slide-over-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="slide-over-body" role="tabpanel">
        {loading && <div className="profile-loading-spinner">Loading profile…</div>}
        {error && !loading && <p className="profile-unavailable">{error}</p>}
        {!loading && !error && !profile && (
          <p className="text-muted">No linked profile data.</p>
        )}
        {!loading && !error && profile && tab === 'overview' && (
          <OverviewTab
            profile={profile}
            profileTickets={profileTickets}
            profileTicketsLoading={profileTicketsLoading}
            siblings={siblings}
            siblingsLoading={siblingsLoading}
            linkedLearnerId={linkedLearnerId}
            linkedLearnerName={linkedLearnerName}
            linkedEnrollmentId={linkedEnrollmentId}
            currentTicketId={currentTicketId}
            onTicketNavigate={onTicketNavigate}
          />
        )}
        {!loading && !error && profile && tab === 'enrollments' && (
          <EnrollmentsTab profile={profile} />
        )}
        {!loading && !error && profile && tab === 'courses' && <CoursesTab profile={profile} />}
        {!loading && !error && profile && tab === 'payments' && <PaymentsTab profile={profile} />}
        {tab === 'platforms' && (
          <PlatformsTab presence={presence} loading={presenceLoading} error={presenceError} />
        )}
      </div>
    </div>
  );
}
